import './DashboardEditModal.css';

import React, { useContext, useEffect, useState } from 'react';
import B2BPopUpForm from '../../b2b-common/B2BPopUpForm';
import EditText from '../../b2b-common/EditText';
import BooleanIconToggle from '../../b2b-common/BooleanIconToggle';
import { generateClassName, handleObjectFieldChange } from '../../b2b-common/helpers';
import { Clear, Edit, EditOff, Lock, LockOpen, Public, PublicOff } from '@mui/icons-material';
import FieldComponent from '../../b2b-common/FieldComponent';
import { B2BContentContext } from '../../layout/Layout';
import { LoginContext } from '../../../App';
import { showNotification } from '../../b2b-common/Notification';
import { saveNewDashboard, updateDashboard } from '../../../services/b2b-reporting-apiServices';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';
import { Box } from '@mui/material';
import useRolesAndProductsCheck from '../../b2b-common/hooks/useRolesAndProductsCheck';

export const DashboardConfiguration = ({ dashboard, setDashboard, readOnly = false, className = null }) => {
    const { t } = useB2BTranslation('b2b-reporting');
    const { checkProductsPresence } = useRolesAndProductsCheck();
    return (
        <Box className={generateClassName('dashboard-edit-configuration', '', className)}
            display={'flex'} flexDirection='column' gap={'20px'} alignSelf={'flex-start'}
        >
            <Box className='dashboard-dimensions' display={'flex'} flexDirection='row' gap={'20px'} alignSelf={'flex-start'}>
                <EditText object={dashboard} objectField={'dashboardConfig.rows'}
                    setObjectField={setDashboard} label={t('Rows')}
                    className={'dashboard-rows-columns'} suffix={t('rows')} type='number'
                    readOnly={readOnly}
                    minValue={1}
                    maxValue={10}
                />
                <Clear />
                <EditText object={dashboard} objectField={'dashboardConfig.columns'}
                    setObjectField={setDashboard} label={t('Columns')}
                    className={'dashboard-rows-columns'} suffix={t('columns')} type='number'
                    readOnly={readOnly}
                    minValue={1}
                    maxValue={8}
                />
            </Box>
            {checkProductsPresence(['B2B_CUSTOM_DASHBOARDS_PUBLIC']) && (
                <Box className='dashboard-public' display={'flex'} flexDirection='row' gap={'20px'} alignSelf={'flex-start'} width={'100%'}>
                    <FieldComponent label={t('Public Dashboard')} className='dashboard-public-field' component={
                        <BooleanIconToggle
                            value={dashboard.publicDashboard}
                            onChange={(value) => handleObjectFieldChange('publicDashboard', value, setDashboard)}
                            trueIcon={<Public />}
                            trueText={t('Public Dashboard')}
                            falseIcon={<PublicOff />}
                            falseText={t('Not Public Dashboard')}
                            className='dashboard-publicoggle'
                        />
                    } />
                    {dashboard.publicDashboard && (
                        <FieldComponent label={t('Password Protected')} className='dashboard-public-password-field' component={
                            <Box display={'flex'} flexDirection='row' gap={'20px'} alignSelf={'center'} width={'100%'}>
                                <BooleanIconToggle
                                    value={dashboard.dashboardPassword === null}
                                    onChange={(value) => handleObjectFieldChange('dashboardPassword', !value ? '' : null, setDashboard)}
                                    trueIcon={<LockOpen />}
                                    trueText={t('Not Password Protected')}
                                    falseIcon={<Lock />}
                                    falseText={t('Password Protected')}
                                    className='dashboard-public-password-toggle'
                                />
                                {dashboard.dashboardPassword !== null && (
                                    <EditText object={dashboard} objectField={'dashboardPassword'}
                                        setObjectField={setDashboard} //label={t('Password')}
                                        className={'dashboard-password-field'} // type='password'
                                        readOnly={readOnly || dashboard.dashboardPassword === null}
                                        disabled={dashboard.dashboardPassword === null}
                                        onChange={(value) => handleObjectFieldChange('dashboardPassword', value, setDashboard)}
                                    />
                                )}
                            </Box>
                        } />
                    )}
                </Box>
            )}
        </Box>
    );
};

const DashboardEditModal = ({ dashboard = null, open, onSave, onCancel, onDelete }) => {
    const { setNotification } = useContext(B2BContentContext);
    const { loginData } = useContext(LoginContext);
    const { t } = useB2BTranslation('b2b-reporting');

    const newDashboard = {
        "dashboardId": null,
        "dashboardName": t('New Dashboard'),
        "dashboardDescription": t('Dashboard Description'),
        "dashboardConfig": {
            "rows": 3,
            "columns": 3
        },
        "readOnly": false,
        "publicDashboard": false,
        "dashboardPassword": null,
    };

    const [currentDashboard, setCurrentDashboard] = useState(dashboard ? dashboard : newDashboard);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (dashboard !== null) {
            setCurrentDashboard(dashboard);
        }
    }, [dashboard]);

    const handleSave = async () => {
        if (dashboard === null) {
            // create new dashboard
            try {
                const newDashboard = await saveNewDashboard(loginData.accessToken, loginData.loggedOrganizationUuid, currentDashboard);
                showNotification(setNotification, t('Dashboard created'), 'success', 3000);
                // console.log('newDashboard:', newDashboard);
                onSave && onSave(newDashboard);
            } catch (error) {
                setError(error);
                console.error('Error saving new dashboard:', error);
                showNotification(setNotification, t('Error saving new dashboard: ') + error.message, 'error', 3000);
            }
        } else {
            // update existing dashboard
            try {
                const updatedDashboard = await updateDashboard(loginData.accessToken, loginData.loggedOrganizationUuid, currentDashboard);
                showNotification(setNotification, t('Dashboard updated'), 'success', 3000);
                // console.log('updatedDashboard:', updatedDashboard);
                onSave && onSave(updatedDashboard);
            } catch (error) {
                setError(error);
                console.error('Error updating dashboard:', error);
                showNotification(setNotification, t('Error updating dashboard: ') + error.message, 'error', 3000);
            }
        }
    }

    return (
        <B2BPopUpForm
            open={open}
            title={dashboard ? t('Edit Dashboard') : t('Create Dashboard')}
            onClose={onCancel}
            className={'dashboard-edit-modal'}
            buttons={[
                { label: t('Save'), onClick: handleSave },
                { label: t('Cancel'), onClick: onCancel },
            ]}
        >
            <div className='dashboard-fields-row'>
                <EditText object={currentDashboard} objectField={'dashboardName'} setObjectField={setCurrentDashboard} label={t('Name')} className={'dashboard-field'} />
                <FieldComponent label={t('Read only')} className='dashboard-readonly-field' component={
                    <BooleanIconToggle
                        value={currentDashboard.readOnly}
                        onChange={(value) => handleObjectFieldChange('readOnly', value, setCurrentDashboard)}
                        trueIcon={<EditOff />}
                        trueText={t('Read only')}
                        falseIcon={<Edit />}
                        falseText={t('Edit mode')}
                        className='dashboard-read-only-toggle'
                    />
                } />
            </div>
            <div className='dashboard-fields-row'>
                <EditText object={currentDashboard} objectField={'dashboardDescription'} setObjectField={setCurrentDashboard} label={t('Description')} className={'dashboard-field'} multiline={true} rows={5} />
                <DashboardConfiguration dashboard={currentDashboard} setDashboard={setCurrentDashboard} />
            </div>
        </B2BPopUpForm>
    );
};

export default DashboardEditModal;